import React, { useMemo } from "react";

import PageHead from "../../components/PageHead";
import * as api from "./api";
import OfferPicker from "./OfferPicker";
import { Async, Kpi, KpiRow, Panel } from "./panels";
import { useEndpoint } from "./useOffer";

/**
 * Workstation · Landing Pages (§4, tab 4 of 8).
 *
 * `design/system/audits/workstation-tabs.md` §4
 * ==============================================
 * `workstation.md:47-50` asks for: LP inventory + benchmarks + heatmap
 * intelligence. The first two are real and live:
 *
 *   GET /api/offers/{offer_id}/landing-page-variants   creative_engine.py:2252
 *   GET /api/winner-rotator                             creative_engine.py:2304
 *
 * `/api/winner-rotator` IS THE LP-PERFORMANCE SURFACE — it is not named
 * "landing pages", which the audit notes is why the original build brief
 * missed it. It groups every creative with a `landing_page_variant` set by
 * `(offer_id, variant_id)`, aggregates spend/clicks/impressions/conversions/
 * revenue, and declares a winner once a variant clears the spend floor and
 * beats the runner-up by >=10% CPA (`services/winner_rotator.py:154-230`).
 * Its response is offer-grouped, so this tab filters client-side to the
 * selected offer — the same shape the audit documents.
 *
 * HEATMAP INTELLIGENCE IS THE ONE GENUINE GAP ON THIS TAB
 * =========================================================
 * No page-heatmap tooling exists anywhere in the tree — no Hotjar, no
 * Clarity, no scroll-depth/click-map capture (grepped across all of
 * `backend/`). The two endpoints containing the word "heatmap" are
 * creative-intelligence heatmaps (hook-type x performance, format x hook),
 * not page heatmaps, and do not belong here (audit §4). This renders as the
 * labelled placeholder the audit calls for — not a fabricated chart.
 */

function VariantRow({ variant, winnerVariantId }) {
  const m = variant.metrics || {};
  const isWinner = Boolean(winnerVariantId) && variant.variant_id === winnerVariantId;
  return (
    <tr>
      <td>{variant.label || variant.variant_id}</td>
      <td>{m.spend !== undefined ? `$${Number(m.spend).toFixed(2)}` : "—"}</td>
      <td>{m.conversions ?? "—"}</td>
      <td>{m.cpa !== undefined && m.cpa !== null ? `$${Number(m.cpa).toFixed(2)}` : "—"}</td>
      <td>{m.roas !== undefined && m.roas !== null ? Number(m.roas).toFixed(2) : "—"}</td>
      <td>
        {isWinner ? (
          <span className="ws-tag" style={{ color: "var(--cyan, #54cfe6)" }}>Winner</span>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

function Benchmarks({ offerId }) {
  const rotator = useEndpoint(() => api.getWinnerRotator({ days: 30 }), []);

  const offerBlock = useMemo(() => {
    if (rotator.state !== "ready") return null;
    const offers = rotator.data?.offers || [];
    return offers.find((o) => o.offer_id === offerId) || null;
  }, [rotator.state, rotator.data, offerId]);

  return (
    <Panel
      title="Per-variant performance"
      subtitle="30-day spend/CPA/ROAS per landing-page variant, winner declared once it clears the spend floor and beats the runner-up by ≥10% CPA."
    >
      <Async
        result={rotator}
        empty="No landing-page-variant performance recorded across the account yet."
      >
        {() => {
          if (!offerBlock || !Array.isArray(offerBlock.variants) || offerBlock.variants.length === 0) {
            return (
              <div className="state ws-state">
                No landing-page-variant performance for this offer yet. The Winner Rotator only
                reports on creatives that carry a <code>landing_page_variant</code> — this offer
                has none with spend in the last 30 days.
              </div>
            );
          }
          return (
            <div className="ws-table-scroll">
              <table className="tbl ws-table">
                <thead>
                  <tr>
                    <th>Variant</th>
                    <th>Spend</th>
                    <th>Conversions</th>
                    <th>CPA</th>
                    <th>ROAS</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {offerBlock.variants.map((v, i) => (
                    <VariantRow
                      key={v.variant_id || i}
                      variant={v}
                      winnerVariantId={offerBlock.status === "WINNER" ? offerBlock.winner_variant_id : null}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          );
        }}
      </Async>
    </Panel>
  );
}

function Inventory({ offerId }) {
  const variants = useEndpoint(() => api.getLandingPageVariants(offerId), [offerId], {
    skip: !offerId,
  });

  return (
    <Panel title="Landing-page inventory" subtitle="The structured variants configured on this offer.">
      <Async result={variants} empty="No landing-page variants configured on this offer yet.">
        {(data) => {
          const rows = data?.variants || [];
          if (rows.length === 0) {
            return <div className="state ws-state">No landing-page variants configured.</div>;
          }
          return (
            <KpiRow>
              <Kpi label="Variants configured" value={rows.length} />
            </KpiRow>
          );
        }}
      </Async>
    </Panel>
  );
}

export default function LandingPages({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;

  return (
    <>
      <PageHead
        title="Landing Pages"
        subtitle={
          selected
            ? `The pages ${selected.name || "this offer"} sends traffic to, and how they convert.`
            : "The pages traffic lands on, and how they convert."
        }
      />

      <OfferPicker
        offers={offers}
        selectedId={selectedId}
        onSelect={select}
        error={error}
      />

      {!selectedId ? (
        <div className="state ws-state" role="status">
          Select an offer to see its landing pages.
        </div>
      ) : (
        <div className="ws-analytics-grid">
          <Inventory offerId={selectedId} />
          <Benchmarks offerId={selectedId} />
          <Panel title="Heatmap intelligence" subtitle="Page-level scroll/click heatmaps.">
            <div className="state ws-state" role="status">
              <p className="ws-understanding-lead">Not available.</p>
              <p className="dim">
                No page-heatmap tooling exists in Cipher today — no Hotjar, no Clarity, no
                scroll-depth or click-map capture. This is a genuine gap, not a rendering issue:
                adding it would mean standing up new capture infrastructure, which is out of
                scope for this tab. See{" "}
                <code>design/system/audits/workstation-tabs.md</code> §4.
              </p>
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}
